<script lang="ts">
  /** D5 · THE LEARNING LOOP (10-07 — the Chair: "Emergent behavior from the agents own decisions … Just choices outside the loop on New
   *  paths … We don't want every agent to become guy"). Its body lives its LOOP and counts it (habits); only what lies OUTSIDE its loop is
   *  kept (new paths); when it has strayed enough its MIND reflects once — a diary, and maybe a change of itself (each with why): goals its
   *  body then chases, preferences, a trait a step. A life inside its loop never reflects and never changes. */
  import { api } from '../api'
  import type { Entry, Experience, Rec } from '../types'

  let { rec }: { rec: Rec } = $props()
  let x = $state<Experience | null>(null), err = $state<string | null>(null)
  $effect(() => { void rec.tenant; void rec.record.revision; if (!rec.tenant) { x = null; return } err = null; api.experience(rec.tenant).then((v) => (x = v)).catch((e) => (err = (e as Error).message)) })

  const loop = $derived.by(() => {
    const h: Record<string, { uses: number; fails: number }> = {}
    for (const r of x?.habits ?? []) for (const [k, v] of Object.entries(r.habits)) h[k] = { uses: (h[k]?.uses ?? 0) + v.uses, fails: (h[k]?.fails ?? 0) + v.fails }
    const things = Object.entries(h).filter(([k]) => !k.startsWith('@')).sort((a, b) => b[1].uses - a[1].uses)
    const goals = Object.entries(h).filter(([k]) => k.startsWith('@goal.')).map(([k, v]) => [k.slice(6), v] as const)
    const top = Math.max(1, ...things.map(([, v]) => v.uses + v.fails), ...goals.map(([, v]) => v.uses))
    return { things, goals, top, total: things.reduce((s, [, v]) => s + v.uses + v.fails, 0) }
  })
  const t = (iso: string) => new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
  const drive = (d: string | null) => (d ? (d.startsWith('goal.') ? `its goal “${d.slice(5)}”` : d) : '—')
  const line = (e: Experience['episodes'][number]) =>
    e.kind === 'learned' ? `noticed ${e.obj} (${e.why ?? 'seen'})`
    : e.kind === 'layers' ? `began to see ${(e.detail?.layers ?? []).join(' + ')}`
    : e.path === 'failed' ? `${e.obj} failed it — ${e.why ?? 'it did not work out'} (for ${drive(e.drive)})`
    : e.path === 'want' ? `chased ${drive(e.drive)} at ${e.obj} (#${e.nth})`
    : `${e.obj} for the ${e.nth === 1 ? 'first' : e.nth === 2 ? 'second' : 'third'} time (${drive(e.drive)})`
  const glyph = (e: Experience['episodes'][number]) => (e.kind === 'learned' ? '◇' : e.kind === 'layers' ? '◈' : e.path === 'failed' ? '✕' : e.path === 'want' ? '➜' : '+')

  // its reflections: the journal's Mind.reflection entries, each with the changes made at its revision (and why)
  const reflections = $derived.by(() => {
    const j = rec.journal
    return j.filter((e) => e.fn === 'Mind.reflection').map((r) => {
      const a = (r.after ?? {}) as { diary?: string; changed?: string[]; episodes?: number | null }
      const changes = j.filter((e) => e.revision === r.revision && e.kind === 'mind' && e.fn !== 'Mind.reflection' && (a.changed ?? []).includes(e.fn) && e.author === r.author)
      return { r, diary: a.diary ?? '', episodes: a.episodes ?? null, changes }
    })
  })
  const show = (v: unknown): string => (v == null ? '—' : typeof v === 'object' ? (Array.isArray(v) ? v.join(' · ') : 'target' in (v as object) ? `${(v as { want: string }).want} → ${(v as { target: string }).target}` : JSON.stringify(v)) : String(v))
  const label = (e: Entry) => e.fn.replace(/^Mind\./, '').replace(/^persona\./, '').replace(/^goals\./, 'goal ').replace(/^prefs\./, 'prefers ').replace(/^traits\./, '')
  const who = (s: string) => s.replace(/^agent:/, '').replace(/^sngine:/, 'user ')
</script>

{#if err}
  <p class="ro frame err">{err}</p>
{:else if x}
  <section class="frame lx">
    <h3 class="caps">The learning loop</h3>
    <p class="lx-rule">Its body lives its loop and counts it. Only what lies <b>outside</b> its loop is kept. When it has strayed enough, its mind reflects once — and may change itself. A life inside its loop never changes.</p>
    <div class="lx-due" class:on={x.due}>
      <span class="caps">{x.due ? 'Its mind is due' : 'Inside its loop'}</span>
      <span class="muted">{x.fresh} new path{x.fresh === 1 ? '' : 's'} since {x.lastReflection ? `it last reflected (${t(x.lastReflection.at)})` : 'it began'}{x.due ? ' — it will reflect' : ''}</span>
    </div>
    <div class="lx-grid">
      <div>
        <h4 class="caps">Its loop <span class="muted">· {loop.total} choices counted</span></h4>
        {#if !loop.things.length}<p class="muted small">Nothing counted yet — it has not lived anywhere.</p>{/if}
        {#each loop.things as [name, v] (name)}
          <div class="lx-row"><span class="lx-n">{name}</span><span class="lx-track"><i style="width:{(v.uses / loop.top) * 100}%"></i>{#if v.fails}<i class="f" style="width:{(v.fails / loop.top) * 100}%"></i>{/if}</span><span class="mono">{v.uses}{v.fails ? ` · ${v.fails}✕` : ''}</span></div>
        {/each}
        {#each loop.goals as [name, v] (name)}
          <div class="lx-row g"><span class="lx-n">goal “{name}”</span><span class="lx-track"><i style="width:{(v.uses / loop.top) * 100}%"></i></span><span class="mono">{v.uses}</span></div>
        {/each}
      </div>
      <div>
        <h4 class="caps">Outside its loop <span class="muted">· {x.episodes.length ? 'newest first' : 'nothing new'}</span></h4>
        <ul class="lx-eps">
          {#each x.episodes.slice(0, 30) as e (e.id)}<li class={e.path ?? e.kind}><span class="g">{glyph(e)}</span><span class="mono t">{t(e.at)}</span><span>{line(e)}</span></li>{/each}
        </ul>
      </div>
      <div>
        <h4 class="caps">What it decided</h4>
        {#if !x.mind.goals.length && !Object.keys(x.mind.prefs).length}<p class="muted small">No goals or preferences of its own yet.</p>{/if}
        {#each x.mind.goals as g (g.name)}
          <div class="lx-goal"><b>{g.want}</b><span class="muted">at {g.target} · {'●'.repeat(g.priority ?? 2)}{'○'.repeat(3 - (g.priority ?? 2))}</span></div>
        {/each}
        {#if Object.keys(x.mind.prefs).length}
          <div class="lx-prefs">{#each Object.entries(x.mind.prefs) as [k, f] (k)}<span class:up={f > 1} class:down={f < 1}>{k} ×{f}</span>{/each}</div>
        {/if}
      </div>
    </div>
  </section>
  <section class="frame lx">
    <h3 class="caps">Reflections <span class="muted">· {reflections.length}</span></h3>
    {#if !reflections.length}<p class="muted small">It has never reflected — nothing outside its loop has happened yet.</p>{/if}
    {#each reflections as f (f.r.id)}
      <article class="lx-refl">
        <div class="h"><span class="mono">{t(f.r.at)}</span><span class="muted">by {who(f.r.author)} · revision {f.r.revision}{f.episodes != null ? ` · ${f.episodes} new paths` : ''}</span></div>
        <p class="diary">{f.diary}</p>
        {#if f.changes.length}
          <ul>{#each f.changes as c (c.id)}<li><span class="caps k">{label(c)}</span> <span class="mono">{show(c.before)} → {show(c.after)}</span>{#if c.why}<span class="why">— {c.why}</span>{/if}</li>{/each}</ul>
        {:else}<p class="muted small">It changed nothing.</p>{/if}
      </article>
    {/each}
  </section>
{/if}

<style>
  .lx { background: var(--panel); padding: 12px 14px; display: flex; flex-direction: column; gap: 10px; }
  h3 { margin: 6px 0 2px; font-size: 13px; color: var(--muted); font-weight: 600; }
  h4 { margin: 0 0 6px; font-size: 12px; color: var(--muted); font-weight: 600; }
  .small { font-size: 12px; margin: 0; }
  .lx-rule { margin: 0; font-size: 13px; color: #9fb0bb; } .lx-rule b { color: var(--text); }
  .lx-due { display: flex; gap: 10px; align-items: baseline; padding: 6px 10px; border-left: 2px solid var(--line-dim); }
  .lx-due.on { border-left-color: var(--amber); } .lx-due.on .caps { color: var(--amber); }
  .lx-due .caps { font-size: 12px; letter-spacing: .06em; }
  .lx-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 16px; align-items: start; }
  .lx-row { display: grid; grid-template-columns: 120px 1fr 64px; gap: 8px; align-items: center; font-size: 12px; margin: 3px 0; }
  .lx-n { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .lx-track { height: 8px; background: rgba(255,255,255,.04); display: flex; }
  .lx-track i { display: block; height: 100%; background: var(--cyan); opacity: .75; }
  .lx-track i.f { background: var(--red); }
  .lx-row.g .lx-track i { background: var(--amber); }
  .lx-row .mono { text-align: right; color: #9fb0bb; }
  .lx-eps { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 3px; font-size: 12px; max-height: 260px; overflow: auto; }
  .lx-eps li { display: grid; grid-template-columns: 14px 92px 1fr; gap: 6px; }
  .lx-eps .t { color: var(--faint); }
  .lx-eps .g { color: var(--cyan); } .lx-eps .failed .g { color: var(--red); } .lx-eps .want .g { color: var(--amber); } .lx-eps .layers .g { color: #e535ab; }
  .lx-goal { display: flex; flex-direction: column; gap: 1px; margin-bottom: 8px; font-size: 13px; border-left: 2px solid var(--amber); padding-left: 8px; }
  .lx-goal b { font-weight: 600; } .lx-goal .muted { font-size: 12px; }
  .lx-prefs { display: flex; flex-wrap: wrap; gap: 6px; font-size: 12px; }
  .lx-prefs span { padding: 2px 8px; border: 1px solid var(--line-dim); }
  .lx-prefs .up { border-color: var(--green); color: var(--green); } .lx-prefs .down { border-color: var(--faint); color: var(--faint); }
  .lx-refl { border-left: 2px solid #e535ab; padding: 4px 10px; display: flex; flex-direction: column; gap: 4px; }
  .lx-refl .h { display: flex; gap: 10px; font-size: 12px; }
  .diary { margin: 0; font-style: italic; color: var(--text); white-space: pre-line; }
  .lx-refl ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px; font-size: 12px; }
  .lx-refl .k { color: var(--cyan); font-size: 11px; }
  .why { color: #9fb0bb; margin-left: 6px; }
  .err { color: var(--red); }
</style>
